import { getSupabaseBrowserClient } from "@/lib/supabase/client";
import {
  EVENT_COVER_TYPES,
  EVENT_MEDIA_BUCKET,
  EVENT_PAGE_SIZE,
  isAttendanceStatus,
  isEventStatus,
  isEventType,
  isLocationType,
  type EventAttendee,
  type EventAttendanceStatus,
  type EventCommunity,
  type EventCreateInput,
  type EventDetail,
  type EventListFilters,
  type EventOrganizer,
  type EventStatus,
  type EventSummary,
  type EventUpdateInput,
} from "@/lib/events/types";
import { resolveEventCoverUrl } from "@/lib/events/covers";
import { asObject, createId, getErrorMessage, safeStorageExtension } from "@/lib/utils";

const EVENT_LIST_SELECT = `
  id,
  title,
  slug,
  short_description,
  cover_path,
  event_type,
  starts_at,
  ends_at,
  timezone,
  location_type,
  physical_location,
  organizer_user_id,
  organizer_community_id,
  max_attendees,
  registration_deadline,
  attending_count,
  status,
  organizer:profiles!events_organizer_user_id_fkey (
    id,
    full_name
  ),
  community:communities!events_organizer_community_id_fkey (
    id,
    name,
    slug,
    visibility
  )
`;

const EVENT_DETAIL_SELECT = `
  ${EVENT_LIST_SELECT},
  description,
  agenda,
  published_at,
  cancelled_at,
  deleted_at
`;

type ProfileRef = { id: string; full_name: string };
type CommunityRef = {
  id: string;
  name: string;
  slug: string;
  visibility?: "public" | "closed" | "private" | "service" | null;
};

type EventRow = {
  id: string;
  title: string;
  slug: string;
  short_description: string | null;
  description?: string | null;
  agenda?: string | null;
  cover_path: string | null;
  event_type: string;
  starts_at: string;
  ends_at: string | null;
  timezone: string;
  location_type: string;
  physical_location: string | null;
  organizer_user_id: string;
  organizer_community_id: string | null;
  max_attendees: number | null;
  registration_deadline: string | null;
  attending_count: number;
  status: string;
  published_at?: string | null;
  cancelled_at?: string | null;
  deleted_at?: string | null;
  organizer: ProfileRef | ProfileRef[] | null;
  community: CommunityRef | CommunityRef[] | null;
};

function sanitizeSearch(value: string) {
  return value.replace(/[%_,]/g, " ").trim();
}

function mapOrganizer(value: ProfileRef | ProfileRef[] | null): EventOrganizer | null {
  const row = asObject(value);
  if (!row) {
    return null;
  }
  return { id: row.id, fullName: row.full_name };
}

function mapCommunity(
  value: CommunityRef | CommunityRef[] | null,
): EventCommunity | null {
  const row = asObject(value);
  if (!row) {
    return null;
  }
  return { id: row.id, name: row.name, slug: row.slug, visibility: row.visibility ?? null };
}

function mapEventSummary(
  row: EventRow,
  coverUrls: Map<string, string>,
  attendance: Map<string, EventAttendanceStatus>,
  canManage = false,
): EventSummary | null {
  if (!isEventType(row.event_type) || !isLocationType(row.location_type)) {
    return null;
  }
  if (!isEventStatus(row.status)) {
    return null;
  }

  return {
    id: row.id,
    title: row.title,
    slug: row.slug,
    shortDescription: row.short_description,
    coverPath: row.cover_path,
    coverUrl: resolveEventCoverUrl(
      row.cover_path ? coverUrls.get(row.cover_path) ?? null : null,
      row.slug,
      row.event_type,
    ),
    eventType: row.event_type,
    startsAt: row.starts_at,
    endsAt: row.ends_at,
    timezone: row.timezone,
    locationType: row.location_type,
    physicalLocation: row.physical_location,
    organizer: mapOrganizer(row.organizer),
    community: mapCommunity(row.community),
    maxAttendees: row.max_attendees,
    registrationDeadline: row.registration_deadline,
    attendingCount: row.attending_count,
    status: row.status,
    myAttendance: attendance.get(row.id) ?? null,
    canManage,
  };
}

export async function signEventMedia(paths: (string | null)[]) {
  const uniquePaths = [...new Set(paths.filter((path): path is string => Boolean(path)))];
  const urls = new Map<string, string>();

  if (uniquePaths.length === 0) {
    return urls;
  }

  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.storage
    .from(EVENT_MEDIA_BUCKET)
    .createSignedUrls(uniquePaths, 60 * 60);

  if (error) {
    return urls;
  }

  for (const item of data ?? []) {
    if (item.path && item.signedUrl) {
      urls.set(item.path, item.signedUrl);
    }
  }

  return urls;
}

async function loadMyAttendance(eventIds: string[]) {
  const attendance = new Map<string, EventAttendanceStatus>();
  if (eventIds.length === 0) {
    return attendance;
  }

  const supabase = getSupabaseBrowserClient();
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) {
    return attendance;
  }

  const { data, error } = await supabase
    .from("event_attendees")
    .select("event_id, status")
    .eq("user_id", user.id)
    .in("event_id", eventIds);

  if (error) {
    throw error;
  }

  for (const row of data ?? []) {
    if (isAttendanceStatus(row.status)) {
      attendance.set(row.event_id, row.status);
    }
  }

  return attendance;
}

async function hydrateEvents(rows: EventRow[], manageIds?: Set<string>) {
  const [coverUrls, attendance] = await Promise.all([
    signEventMedia(rows.map((row) => row.cover_path)),
    loadMyAttendance(rows.map((row) => row.id)),
  ]);

  return rows
    .map((row) =>
      mapEventSummary(row, coverUrls, attendance, manageIds?.has(row.id) ?? false),
    )
    .filter((event): event is EventSummary => Boolean(event));
}

function applyEventFilters(
  // PostgREST filter builder; the generated client types are too narrow here.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  query: any,
  filters: EventListFilters,
) {
  const search = sanitizeSearch(filters.search);
  if (search.length >= 2) {
    query = query.or(
      `title.ilike.%${search}%,short_description.ilike.%${search}%`,
    );
  }
  if (filters.eventType !== "all") {
    query = query.eq("event_type", filters.eventType);
  }
  if (filters.locationType !== "all") {
    query = query.eq("location_type", filters.locationType);
  }
  if (filters.communityId !== "all") {
    query = query.eq("organizer_community_id", filters.communityId);
  }
  return query;
}

function upcomingUpperBound(timeWindow: EventListFilters["timeWindow"]) {
  const now = new Date();
  if (timeWindow === "today") {
    const tomorrow = new Date(now);
    tomorrow.setHours(24, 0, 0, 0);
    return tomorrow.toISOString();
  }
  if (timeWindow === "week") {
    return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString();
  }
  if (timeWindow === "month") {
    return new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();
  }
  return null;
}

export async function canCreateEvents() {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("can_create_events");
  if (error) {
    throw error;
  }
  return Boolean(data);
}

export async function getEventsByIds(ids: string[]): Promise<EventSummary[]> {
  const uniqueIds = [...new Set(ids.filter(Boolean))];
  if (uniqueIds.length === 0) {
    return [];
  }

  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase
    .from("events")
    .select(EVENT_LIST_SELECT)
    .in("id", uniqueIds);

  if (error) {
    throw error;
  }
  return hydrateEvents((data ?? []) as unknown as EventRow[]);
}

export async function getUpcomingEvents(
  filters: EventListFilters,
): Promise<EventSummary[]> {
  const supabase = getSupabaseBrowserClient();
  const now = new Date().toISOString();
  let query = supabase
    .from("events")
    .select(EVENT_LIST_SELECT)
    .eq("status", "published")
    .is("deleted_at", null)
    .or(`ends_at.gte.${now},and(ends_at.is.null,starts_at.gte.${now})`)
    .order("starts_at", { ascending: true })
    .limit(60);

  query = applyEventFilters(query, filters);
  const until = upcomingUpperBound(filters.timeWindow);
  if (until) {
    query = query.lte("starts_at", until);
  }

  const { data, error } = await query;
  if (error) {
    throw error;
  }
  return hydrateEvents((data ?? []) as unknown as EventRow[]);
}

export async function getPastEvents(input: {
  filters: EventListFilters;
  offset?: number;
}): Promise<EventSummary[]> {
  const supabase = getSupabaseBrowserClient();
  const now = new Date().toISOString();
  const offset = input.offset ?? 0;
  let query = supabase
    .from("events")
    .select(EVENT_LIST_SELECT)
    .in("status", ["published", "cancelled", "completed"])
    .is("deleted_at", null)
    .or(`ends_at.lt.${now},and(ends_at.is.null,starts_at.lt.${now})`)
    .order("starts_at", { ascending: false })
    .range(offset, offset + EVENT_PAGE_SIZE - 1);

  query = applyEventFilters(query, input.filters);

  const { data, error } = await query;
  if (error) {
    throw error;
  }
  return hydrateEvents((data ?? []) as unknown as EventRow[]);
}

export async function getMyEvents(): Promise<EventSummary[]> {
  const supabase = getSupabaseBrowserClient();
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();
  if (authError) {
    throw authError;
  }
  if (!user) {
    return [];
  }

  const { data, error } = await supabase
    .from("event_attendees")
    .select(
      `
      status,
      event:events!event_attendees_event_id_fkey (
        ${EVENT_LIST_SELECT}
      )
    `,
    )
    .eq("user_id", user.id)
    .order("created_at", { ascending: false })
    .limit(100);

  if (error) {
    throw error;
  }

  const rows = (data ?? [])
    .map((item) => asObject(item.event as EventRow | EventRow[] | null))
    .filter((row): row is EventRow => Boolean(row));

  const events = await hydrateEvents(rows);
  return events.sort(
    (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime(),
  );
}

export async function getCommunityEvents(
  communityId: string,
): Promise<EventSummary[]> {
  const supabase = getSupabaseBrowserClient();
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from("events")
    .select(EVENT_LIST_SELECT)
    .eq("organizer_community_id", communityId)
    .eq("status", "published")
    .is("deleted_at", null)
    .or(`ends_at.gte.${now},and(ends_at.is.null,starts_at.gte.${now})`)
    .order("starts_at", { ascending: true })
    .limit(12);

  if (error) {
    throw error;
  }
  return hydrateEvents((data ?? []) as unknown as EventRow[]);
}

export async function getAdminEvents(): Promise<EventSummary[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase
    .from("events")
    .select(EVENT_LIST_SELECT)
    .order("starts_at", { ascending: false })
    .limit(100);

  if (error) {
    throw error;
  }
  return hydrateEvents((data ?? []) as unknown as EventRow[]);
}

async function getEventRow(column: "slug" | "id", value: string) {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase
    .from("events")
    .select(EVENT_DETAIL_SELECT)
    .eq(column, value)
    .maybeSingle();

  if (error) {
    throw error;
  }
  if (!data) {
    return null;
  }

  const row = data as unknown as EventRow;
  const [events, joinResult, manageResult] = await Promise.all([
    hydrateEvents([row]),
    supabase.rpc("get_event_join_url", { p_event_id: row.id }),
    supabase.rpc("can_manage_event", { target_event_id: row.id }),
  ]);

  const summary = events[0];
  if (!summary) {
    return null;
  }

  if (joinResult.error) {
    throw joinResult.error;
  }
  if (manageResult.error) {
    throw manageResult.error;
  }

  return {
    ...summary,
    canManage: Boolean(manageResult.data),
    description: row.description ?? null,
    agenda: row.agenda ?? null,
    joinUrl: (joinResult.data as string | null) ?? null,
    publishedAt: row.published_at ?? null,
    cancelledAt: row.cancelled_at ?? null,
    deletedAt: row.deleted_at ?? null,
  } satisfies EventDetail;
}

export async function getEventBySlug(slug: string): Promise<EventDetail | null> {
  return getEventRow("slug", slug);
}

export async function getEventById(id: string): Promise<EventDetail | null> {
  return getEventRow("id", id);
}

export async function getEventAttendees(eventId: string): Promise<EventAttendee[]> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase
    .from("event_attendees")
    .select(
      `
      user_id,
      status,
      updated_at,
      profile:profiles!event_attendees_user_id_fkey (
        full_name
      )
    `,
    )
    .eq("event_id", eventId)
    .order("updated_at", { ascending: false });

  if (error) {
    throw error;
  }

  return (data ?? []).flatMap((row) => {
    if (!isAttendanceStatus(row.status)) {
      return [];
    }
    const profile = asObject(
      row.profile as { full_name: string } | { full_name: string }[] | null,
    );
    return [
      {
        userId: row.user_id,
        fullName: profile?.full_name ?? "Ismeretlen felhasználó",
        status: row.status,
        updatedAt: row.updated_at,
      } satisfies EventAttendee,
    ];
  });
}

export function attendeesToCsv(attendees: EventAttendee[]) {
  const header = "Név;Státusz;Frissítve";
  const lines = attendees.map((attendee) =>
    [
      attendee.fullName.replaceAll(";", ","),
      attendee.status,
      attendee.updatedAt,
    ].join(";"),
  );
  return [header, ...lines].join("\n");
}

export async function uploadEventCover(input: { userId: string; file: File }) {
  if (!EVENT_COVER_TYPES.includes(input.file.type as (typeof EVENT_COVER_TYPES)[number])) {
    throw new Error("Csak JPG, PNG vagy WebP borítókép tölthető fel.");
  }

  const supabase = getSupabaseBrowserClient();
  const extension = safeStorageExtension(input.file.name, "jpg");
  const path = `${input.userId}/cover-${createId()}.${extension}`;
  const { error } = await supabase.storage
    .from(EVENT_MEDIA_BUCKET)
    .upload(path, input.file, {
      cacheControl: "3600",
      contentType: input.file.type,
      upsert: false,
    });

  if (error) {
    throw error;
  }

  return path;
}

export async function removeEventCover(path: string | null) {
  if (!path) {
    return null;
  }
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.storage.from(EVENT_MEDIA_BUCKET).remove([path]);
  return error;
}

export async function createEvent(input: EventCreateInput) {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("create_event", {
    p_title: input.title,
    p_short_description: input.shortDescription,
    p_description: input.description,
    p_agenda: input.agenda,
    p_cover_path: input.coverPath,
    p_event_type: input.eventType,
    p_starts_at: input.startsAt,
    p_ends_at: input.endsAt,
    p_location_type: input.locationType,
    p_physical_location: input.physicalLocation,
    p_online_url: input.onlineUrl,
    p_community_id: input.communityId,
    p_max_attendees: input.maxAttendees,
    p_registration_deadline: input.registrationDeadline,
    p_publish: input.publish,
  });

  if (error) {
    throw error;
  }

  const event = await getEventById(data as string);
  return event;
}

export async function updateEvent(input: EventUpdateInput) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("update_event", {
    p_event_id: input.eventId,
    p_title: input.title,
    p_short_description: input.shortDescription,
    p_description: input.description,
    p_agenda: input.agenda,
    p_cover_path: input.coverPath,
    p_event_type: input.eventType,
    p_starts_at: input.startsAt,
    p_ends_at: input.endsAt,
    p_location_type: input.locationType,
    p_physical_location: input.physicalLocation,
    p_online_url: input.onlineUrl,
    p_max_attendees: input.maxAttendees,
    p_registration_deadline: input.registrationDeadline,
  });

  if (error) {
    throw error;
  }
}

export async function setEventLifecycle(eventId: string, status: EventStatus) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("set_event_lifecycle", {
    p_event_id: eventId,
    p_status: status,
  });
  if (error) {
    throw error;
  }
}

export async function softDeleteEvent(eventId: string) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("soft_delete_event", {
    p_event_id: eventId,
  });
  if (error) {
    throw error;
  }
}

export async function setEventAttendance(
  eventId: string,
  status: EventAttendanceStatus,
) {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("set_event_attendance", {
    p_event_id: eventId,
    p_status: status,
  });
  if (error) {
    throw error;
  }
  return data as EventAttendanceStatus;
}

export async function shareEventToFeed(input: {
  eventId: string;
  content: string;
  audience: "global" | "community";
  communityId: string | null;
}) {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("share_event_to_feed", {
    p_event_id: input.eventId,
    p_content: input.content,
    p_audience: input.audience,
    p_community_id: input.communityId,
  });
  if (error) {
    throw error;
  }
  return data as string;
}

export async function prepareEventReminders() {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("prepare_event_reminders");
  if (error) {
    throw error;
  }
  return (data as number) ?? 0;
}

export { getErrorMessage };
